import { type Observation, referenceAsPrice, shownReference } from '../observation/observation';
import { resolveRevenue } from '../observation/revenue';
import type { CampaignStatus } from './confirmation';
import { REGULAR_PRICE_WINDOW_DAYS } from './constants';
import { restartsOn } from './permanence';
import { discountDepth, isOnPromotion } from './promotion';
import type { CampaignRun } from './wave';

/** `unscraped`: no observation and the vendor was not scraped that day; `absent`: scraped for another SKU but not this one (CONTEXT.md, Campaign run). */
export type DayKind = 'discount' | 'regular' | 'unknown' | 'unscraped' | 'absent';

/** The final fact of a day inside a snapped campaign run of its SKU at its vendor channel (CONTEXT.md, Campaign ledger). */
type DayCampaign = {
  readonly run: CampaignRun;
  readonly status: CampaignStatus;
};

/** One SKU at one vendor channel on one day of the detection axis. */
export type Day = {
  readonly date: string;
  readonly kind: DayKind;
  /** Positive charged price, else null. */
  readonly price: number | null;
  readonly regularPrice: number | null;
  /** `restart` when the day is its own regular price because the window restarted on a cut. Null when the day had no regular price, or was its own regular price and seeded the window. */
  readonly regularSource: 'reference' | 'modal' | 'restart' | null;
  /** The positive reference the vendor showed that day, of either kind, else null. */
  readonly reference: number | null;
  /** discountDepth(price, regularPrice) when both are known. */
  readonly depth: number | null;
  /** Null units are zero where the stock state is known (CONTEXT.md, Campaign baseline); negative units are zero. */
  readonly unitsSold: number | null;
  /** The observation's decoded stock state: true, false or unknown. */
  readonly inStock: boolean | null;
  readonly revenue: number | null;
  /** Null until the ledger writes the run the day belongs to; a day outside every run stays null. */
  readonly campaign: DayCampaign | null;
};

export function isObserved(day: Day): boolean {
  return day.kind === 'discount' || day.kind === 'regular';
}

/** The ledger's final fact: the day lies in a run that is not a permanent cut (CONTEXT.md, Campaign ledger). */
export function isPromotionDay(
  day: Day
): day is Day & { readonly campaign: NonNullable<Day['campaign']> } {
  return day.campaign !== null && day.campaign.status !== 'permanent';
}

export function mean(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

export function median(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  return sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function shownPrice(obs: Observation): number | null {
  const reference = shownReference(obs.observed);
  return reference === null ? null : referenceAsPrice(reference);
}

function positive(value: number | null | undefined): number | null {
  return value != null && value > 0 ? value : null;
}

// Charged prices of the SKU's last REGULAR_PRICE_WINDOW_DAYS observed regular days, oldest first (spec decision 4).
class RegularPriceWindow {
  private prices: number[] = [];

  push(price: number): void {
    this.prices.push(price);
    if (this.prices.length > REGULAR_PRICE_WINDOW_DAYS) this.prices.shift();
  }

  reset(price: number): void {
    this.prices = [price];
  }

  // Whether the price was charged on a regular day inside the window.
  has(price: number): boolean {
    return this.prices.includes(price);
  }

  // The most frequent price, ties to the most recently pushed (Kehoe and Midrigan running mode).
  mode(): number | null {
    const counts = new Map<number, number>();
    let top = 0;
    for (const price of this.prices) {
      const count = (counts.get(price) ?? 0) + 1;
      counts.set(price, count);
      top = Math.max(top, count);
    }
    for (let i = this.prices.length - 1; i >= 0; i--) {
      if (counts.get(this.prices[i]) === top) return this.prices[i];
    }
    return null;
  }
}

type RegularPriceRuling = {
  readonly regularPrice: number;
  readonly source: Day['regularSource'];
};

// The mode is the regular price unless the window corroborates the shown reference; a reference alone only seeds; a lapsed window restarts (CONTEXT.md, Campaign run).
function regularPriceOf(
  reference: number | null,
  price: number,
  window: RegularPriceWindow,
  regularLapsed: boolean
): RegularPriceRuling {
  if (regularLapsed) return { regularPrice: price, source: 'restart' };
  const modal = window.mode();
  if (reference !== null && (modal === null || window.has(reference))) {
    return { regularPrice: reference, source: 'reference' };
  }
  if (modal !== null) return { regularPrice: modal, source: 'modal' };
  return { regularPrice: price, source: null };
}

function unobservedDay(date: string, scrapedDays: ReadonlySet<string>): Day {
  return {
    date,
    kind: scrapedDays.has(date) ? 'absent' : 'unscraped',
    price: null,
    regularPrice: null,
    regularSource: null,
    reference: null,
    depth: null,
    unitsSold: null,
    inStock: null,
    revenue: null,
    campaign: null,
  };
}

function observedDay(
  date: string,
  obs: Observation,
  price: number | null,
  ruling: RegularPriceRuling | null
): Day {
  const { inStock } = obs.observed;
  const { unitsSold } = obs.signal;
  const regularPrice = ruling?.regularPrice ?? null;
  return {
    date,
    kind:
      price === null || regularPrice === null
        ? 'unknown'
        : isOnPromotion(price, regularPrice)
          ? 'discount'
          : 'regular',
    price,
    regularPrice,
    regularSource: ruling?.source ?? null,
    reference: shownPrice(obs),
    depth: discountDepth(price, regularPrice),
    unitsSold: unitsSold !== null ? Math.max(0, unitsSold) : inStock !== null ? 0 : null,
    inStock,
    revenue: resolveRevenue(obs),
    campaign: null,
  };
}

/** Classify one vendor channel's days over the axis; `scrapedDays` are the days the vendor was scraped for any loaded SKU. */
export function classifyDays(
  dates: readonly string[],
  dayMap: ReadonlyMap<string, Observation>,
  scrapedDays: ReadonlySet<string>
): Day[] {
  const days: Day[] = [];
  const window = new RegularPriceWindow();
  let hasRegularDay = false;
  // First discount day since the last regular day; the cut's start is unknown before the first regular day (CONTEXT.md, Campaign run).
  let cutStart: string | null = null;
  for (const date of dates) {
    const obs = dayMap.get(date);
    if (!obs) {
      days.push(unobservedDay(date, scrapedDays));
      continue;
    }
    const price = positive(obs.observed.price);
    if (price === null) {
      days.push(observedDay(date, obs, null, null));
      continue;
    }
    const regularLapsed = hasRegularDay && cutStart !== null && restartsOn(cutStart, date);
    const ruling = regularPriceOf(shownPrice(obs), price, window, regularLapsed);
    const day = observedDay(date, obs, price, ruling);
    if (day.kind === 'regular') {
      if (ruling.source === 'restart') window.reset(price);
      else window.push(price);
      hasRegularDay = true;
      cutStart = null;
    } else {
      cutStart ??= date;
    }
    days.push(day);
  }
  return days;
}
